<!-- 通用表单组件 -->
<template>
  <div class="form-container">
    <el-form
      ref="formRef"
      :model="formModel"
      :rules="mergedRules"
      :inline="isInline"
      :label-width="`${labelWidth}px`"
      :class="formClasses"
    >
      <template v-for="(comp, index) in formItemsWithState" :key="index">
        <el-form-item
          :prop="comp.prop"
          :rules="comp.rules"
          :class="getFormItemClass(comp)"
        >
          <template #label>
            <span :style="getLabelStyle(comp)">{{ comp.label }}:</span>
          </template>
          <!-- 分组下拉框 -->
          <el-select
            v-if="comp.type === 'groupSelect'"
            :model-value="getFieldValue(comp.prop!)"
            :placeholder="comp.placeholder"
            :style="getControlStyle(comp)"
            :class="{ 'custom-placeholder': comp.placeholderColor }"
            placement="bottom"
            no-data-text="没有相应匹配数据"
            clearable
            :show-arrow="false"
            :teleported="false"
            multiple
            :disabled="comp.disabled ?? false"
            collapse-tags
            collapse-tags-tooltip
            @update:model-value="(val) => updateField(comp.prop!, val, comp)"
            popper-class="group-select-popper"
          >
            <template #default>
              <div class="group-select-dropdown">
                <template v-for="(group, groupIndex) in comp.options" :key="groupIndex">
                  <el-option
                    v-if="!group.options"
                    :label="group.label"
                    :value="group.value"
                  />
                  <div v-else class="group-item">
                    <div class="group-header">
                      <span class="group-title" @click="handleGroupSelectAll(comp, group)">
                        {{ group.label }}
                      </span>
                      <el-icon :class="{ expanded: isGroupExpanded(comp, groupIndex) }" @click.stop="toggleGroup(comp, groupIndex)">
                        <ArrowDown />
                      </el-icon>
                    </div>
                    <div v-show="isGroupExpanded(comp, groupIndex)" class="group-options">
                      <el-option
                        v-for="option in group.options"
                        :key="option.value"
                        :label="option.label"
                        :value="option.value"
                        class="group-option-item"
                      />
                    </div>
                  </div>
                </template>
              </div>
            </template>
          </el-select>
          <!-- 普通输入框和密码框 -->
          <el-input
            v-else-if="comp.type === 'input' || comp.type === 'password'"
            :model-value="getFieldValue(comp.prop)"
            :placeholder="comp.placeholder"
            :style="getControlStyle(comp)"
            :type="comp.type === 'password' ? 'password' : 'text'"
            :class="{ 'custom-placeholder': comp.placeholderColor }"
            :show-password="comp.type === 'password' && comp.showPassword !== false"
            :clearable="comp.clearable ?? true"
            :maxlength="comp.maxlength"
            :disabled="comp.disabled ?? false"
            @update:model-value="(val) => updateField(comp.prop, val, comp)"
          />
          <!-- 普通下拉框 -->
          <el-select
            v-else-if="comp.type === 'select'"
            :model-value="getFieldValue(comp.prop)"
            :placeholder="comp.placeholder"
            :style="getControlStyle(comp)"
            :class="{ 'custom-placeholder': comp.placeholderColor }"
            placement="bottom"
            no-data-text="没有相应匹配数据"
            :clearable="comp.clearable ?? true"
            :disabled="comp.disabled ?? false"
            :show-arrow="false"
            :teleported="true"
            :popper-options="{
              strategy: 'fixed',
              modifiers: [
                { name: 'flip', options: { boundariesElement: 'viewport', fallbackPlacements: ['top', 'bottom'] } },
                { name: 'preventOverflow', options: { boundariesElement: 'viewport' } }
              ]
            }"
            :multiple="comp.multiple || false"
            collapse-tags
            collapse-tags-tooltip
            :filterable="comp.filterable || false"
            :allow-create="comp.allowCreate || false"
            @update:model-value="(val) => updateField(comp.prop, val, comp)"
          >
            <el-option
              v-for="item in comp.options"
              :key="item.value"
              :label="item.label"
              :value="item.value"
              :disabled="item.disabled"
            />
          </el-select>
          <!-- 文本域 -->
          <el-input
            v-else-if="comp.type === 'textarea'"
            :model-value="getFieldValue(comp.prop)"
            :placeholder="comp.placeholder"
            :style="getControlStyle(comp)"
            :class="{ 'custom-placeholder': comp.placeholderColor }"
            type="textarea"
            :rows="comp.rows ?? 3"
            :clearable="comp.clearable ?? true"
            :disabled="comp.disabled ?? false"
            :maxlength="comp.maxlength"
            :autosize="comp.autosize || false"
            @update:model-value="(val) => updateField(comp.prop, val, comp)"
          />
          <!-- 日期选择器 -->
          <el-date-picker
            v-else-if="comp.type === 'date'"
            :model-value="getFieldValue(comp.prop)"
            :type="comp.dateType ?? 'date'"
            :placeholder="comp.placeholder || '选择日期'"
            :style="getControlStyle(comp)"
            :class="{ 'custom-placeholder': comp.placeholderColor }"
            :disabled-date="comp.disabledDate"
            :disabled="comp.disabled ?? false"
            show-footer
            :editable="false"
            :teleported="true"
            placement="bottom-start"
            :popper-options="{
              placement: 'bottom-start',
              strategy: 'fixed',
              modifiers: [
                { name: 'offset', options: { offset: [0, 4] } },
                { name: 'flip', options: { enabled: true, boundariesElement: 'viewport' } },
                { name: 'preventOverflow', options: { boundariesElement: 'viewport' } }
              ]
            }"
            :popper-class="'date-picker-popper'"
            :clearable="comp.clearable ?? true"
            :value-format="getValueFormat(comp)"
            @update:model-value="(val) => updateField(comp.prop, val, comp)"
          />
          <!-- 单选框组 -->
          <el-radio-group
            v-else-if="comp.type === 'radio'"
            :model-value="getFieldValue(comp.prop)"
            :style="getControlStyle(comp)"
            @update:model-value="(val) => updateField(comp.prop, val, comp)"
          >
            <el-radio
              v-for="item in comp.options || []"
              :key="item.value"
              :value="item.value"
              :disabled="item.disabled"
            >
              {{ item.label }}
            </el-radio>
          </el-radio-group>
          <!-- 开关组件 -->
          <el-switch
            v-else-if="comp.type === 'switch'"
            :model-value="getFieldValue(comp.prop, comp)"
            :style="getControlStyle(comp)"
            :disabled="comp.disabled ?? false"
            :active-text="comp.activeText"
            :inactive-text="comp.inactiveText"
            :active-icon="!comp.activeText ? Check : undefined"
            :inactive-icon="!comp.inactiveText ? Close : undefined"
            :active-value="comp.activeValue !== undefined ? comp.activeValue : '1'"
            :inactive-value="comp.inactiveValue !== undefined ? comp.inactiveValue : '0'"
            :size="comp.size ?? 'default'"
            inline-prompt
            @update:model-value="(val) => updateField(comp.prop, val, comp)"
          />
        </el-form-item>
      </template>
    </el-form>
    <div class="form-footer">
      <el-button @click="handleCancel" size="large">重置</el-button>
      <el-button type="primary" @click="handleSubmit" size="large">保存</el-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from 'vue'
import {
  ElForm,
  ElFormItem,
  ElInput,
  ElRadio,
  ElRadioGroup,
  ElSelect,
  ElOption,
  ElButton,
  ElDatePicker,
  ElIcon,
  ElSwitch
} from 'element-plus'
import { ArrowDown,Check,Close } from '@element-plus/icons-vue'
import type { FormInstance, FormRules } from 'element-plus'

// ==================== 类型定义 ====================
interface SelectOption {
  label: string
  value: any
  disabled?: boolean
  options?: SelectOption[]
}

interface FormItem {
  type: 'input' | 'select' | 'textarea' | 'date' | 'radio' | 'groupSelect' | 'switch'
  prop: string
  label: string
  placeholder?: string
  rules?: any
  disabled?: boolean
  clearable?: boolean
  fullWidth?: boolean
  width?: string | number  // 单独设置宽度
  // ========== 新增样式配置 ==========
  // 控件相关样式
  controlWidth?: string | number     // 控件宽度（覆盖全局）
  controlHeight?: string | number    // 控件高度
  controlFontSize?: string | number  // 控件字体大小
  controlColor?: string              // 控件文字颜色
  controlBgColor?: string            // 控件背景色
  // 标签相关样式
  labelFontSize?: string | number    // 标签字体大小
  labelColor?: string                // 标签颜色
  labelFontWeight?: string           // 标签字体粗细（bold, normal等）
  // 占位符样式
  placeholderColor?: string          // 占位符颜色
  // select 相关
  options?: SelectOption[]
  multiple?: boolean
  filterable?: boolean
  allowCreate?: boolean
  // date 相关
  dateType?: 'date' | 'month' | 'year' | 'datetime' | 'week'
  valueFormat?: string
  disabledDate?: (date: Date) => boolean
  // textarea 相关
  rows?: number
  autosize?: boolean
  // 初始值
  initialValue?: any
  defaultValue?: any
  // switch 相关配置
  activeText?: string      // 开启时的文字描述
  inactiveText?: string    // 关闭时的文字描述
  activeValue?: any        // 开启时的值，默认为 true
  inactiveValue?: any      // 关闭时的值，默认为 false
  size?: 'large' | 'default' | 'small'  // 开关大小
}

// ==================== Props 定义 ====================
const props = withDefaults(defineProps<{
  formItems: FormItem[]
  rules?: FormRules
  modelValue?: Record<string, any>
  colsPerRow?: number
  labelWidth?: number
  disabledFields?: string[]
  // 样式配置
  controlWidth?: string | number      // 控件宽度，默认 200
  controlHeight?: string | number     // 控件高度
  fontSize?: string | number          // 字体大小
  labelFontSize?: string | number     // 标签字体大小
  labelColor?: string                 // 标签颜色
}>(), {
  modelValue: () => ({}),
  rules: () => ({}),
  colsPerRow: 1,
  labelWidth: 80,
  disabledFields: () => [],
  controlWidth: 200,  // 默认宽度改为 200
  controlHeight: undefined,
  fontSize: undefined,
  labelFontSize: undefined,
  labelColor: undefined
})

// 分组展开状态管理
const expandedGroupsMap = ref<Map<FormItem, Set<number>>>(new Map())

// 分组展开状态
const getExpandedSet = (item: FormItem): Set<number> => {
  if (!expandedGroupsMap.value.has(item)) expandedGroupsMap.value.set(item, new Set<number>())
  return expandedGroupsMap.value.get(item)!
}

const isGroupExpanded = (item: FormItem, groupIndex: number): boolean => {
  return getExpandedSet(item).has(groupIndex)
}

const toggleGroup = (item: FormItem, groupIndex: number) => {
  const expandedSet = getExpandedSet(item)
  if (expandedSet.has(groupIndex)) expandedSet.delete(groupIndex)
  else expandedSet.add(groupIndex)
}

// 分组全选/取消全选
const handleGroupSelectAll = (item: FormItem, group: SelectOption) => {
  if (!group.options?.length) return
  const groupValues = group.options.map(opt => opt.value)
  const currentValues = getFieldValue(item.prop!) || []
  const isAllSelected = groupValues.every(v => currentValues.includes(v))
  const newValues = isAllSelected
    ? currentValues.filter((v: any) => !groupValues.includes(v))
    : [...new Set([...currentValues, ...groupValues])]
  updateField(item.prop!, newValues, item)
}

// ==================== Emits 定义 ====================
const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, any>): void
  (e: 'change', prop: string, value: any, comp: FormItem): void
  (e: 'submit', data: Record<string, any>): void
  (e: 'cancel'): void
}>()

// ==================== 响应式数据 ====================
const formRef = ref<FormInstance>()

// ==================== 计算属性 ====================
// 是否行内布局（当 colsPerRow > 1 时启用）
const isInline = computed(() => props.colsPerRow && props.colsPerRow > 1)

// 表单样式类
const formClasses = computed(() => ({
  'horizontal-form': isInline.value,
  'grid-form': isInline.value,
  [`cols-${props.colsPerRow || 1}`]: isInline.value
}))

// =========== 获取控件样式（宽度、高度、字体、颜色） ==========
const getControlStyle = (comp: FormItem) => {
  const style: Record<string, string> = {}
  // 宽度优先级：组件单独配置 > 统一配置（默认 200）
  const width = comp.controlWidth ?? comp.width ?? props.controlWidth
  if (width) {
    const widthValue = typeof width === 'number' ? `${width}px` : width
    style.width = widthValue
    style.maxWidth = widthValue
    style.flexShrink = '0'  // 防止被压缩
  }
  // 高度优先级：组件单独配置 > 统一配置
  const height = comp.controlHeight ?? props.controlHeight
  if (height) {
    const heightValue = typeof height === 'number' ? `${height}px` : height
    style.height = heightValue
  }
  // 字体大小优先级：组件单独配置 > 统一配置
  const fontSize = comp.controlFontSize ?? props.fontSize
  if (fontSize) {
    const fontSizeValue = typeof fontSize === 'number' ? `${fontSize}px` : fontSize
    style.fontSize = fontSizeValue
  }
  // 文字颜色 - 使用 CSS 变量
  if (comp.controlColor) style['--control-color'] = comp.controlColor
  // 背景色
  if (comp.controlBgColor) style['--control-bg-color'] = comp.controlBgColor
  // 占位符颜色
  if (comp.placeholderColor) style['--placeholder-color'] = comp.placeholderColor
  //禁用组件时颜色
  if (comp.disabledColor) style['--disabled-color'] = comp.disabledColor
  return style
}

// 获取标签样式
const getLabelStyle = (comp: FormItem) => {
  const style: Record<string, string> = {}
  // 标签字体大小：组件单独配置 > 全局配置
  const labelFontSize = comp.labelFontSize ?? props.labelFontSize
  if (labelFontSize) {
    const fontSizeValue = typeof labelFontSize === 'number' ? `${labelFontSize}px` : labelFontSize
    style.fontSize = fontSizeValue
  }
  // 标签颜色：组件单独配置 > 全局配置
  const labelColor = comp.labelColor ?? props.labelColor
  if (labelColor) style.color = labelColor
  // 标签字体粗细
  if (comp.labelFontWeight) style.fontWeight = comp.labelFontWeight
  return style
}

// 带状态的表单项
const formItemsWithState = computed(() => {
  return props.formItems.map(item => ({
    ...item,
    disabled: item.disabled || props.disabledFields?.includes(item.prop) || false
  }))
})

// 表单模型（用于验证）
const formModel = computed(() => props.modelValue || {})

// 合并验证规则
const mergedRules = computed<FormRules>(() => {
  const rules: FormRules = { ...props.rules }
  props.formItems.forEach(item => {
    if (item.prop && item.rules) {
      rules[item.prop] = item.rules
    }
  })
  return rules
})

// ==================== 方法 ====================
// 获取字段值
const getFieldValue = (prop: string, comp?: FormItem): any => {
  const value = props.modelValue?.[prop]
  // 对于 switch 类型，如果值为 undefined 或 null，返回 inactiveValue
  if (comp?.type === 'switch') {
    const activeValue = comp.activeValue !== undefined ? comp.activeValue : '1';
    const inactiveValue = comp.inactiveValue !== undefined ? comp.inactiveValue : '0';
    // 如果值为 undefined 或 null，返回 inactiveValue
    if (value === undefined || value === null) return inactiveValue;
    // 检查值是否匹配 active-value 或 inactive-value
    if (value === activeValue || value === inactiveValue) return value;
    // 如果值不匹配，尝试进行类型转换
    // 例如：数字 1 和字符串 '1' 的情况
    if (String(value) === String(activeValue)) return activeValue;
    if (String(value) === String(inactiveValue)) return inactiveValue;
    // 如果都不匹配，返回 inactiveValue 作为默认
    return inactiveValue;
  }
  // undefined、null、空数组都返回 undefined
  if (value === undefined || value === null) return undefined
  if (Array.isArray(value) && value.length === 0) return undefined
  return value
}

// 更新字段值
const updateField = (prop: string, value: any, comp: FormItem) => {
  const newModelValue = {
    ...props.modelValue,
    [prop]: value
  }
  emit('update:modelValue', newModelValue)
  emit('change', prop, value, comp)
}

// 获取表单项样式类
const getFormItemClass = (comp: FormItem) => ({
  'full-width-item': comp.fullWidth,
  'grid-item': isInline.value,
  'grid-item-full': comp.type === 'textarea'
})

// 获取日期选择器的值格式
const getValueFormat = (comp: FormItem): string => {
  if (comp.valueFormat) return comp.valueFormat
  const dateType = comp.dateType ?? 'date'
  switch (dateType) {
    case 'month':
      return 'YYYY-MM'
    case 'year':
      return 'YYYY'
    case 'datetime':
      return 'YYYY-MM-DD HH:mm:ss'
    default:
      return 'YYYY-MM-DD'
  }
}

// 表单验证
const validate = async (): Promise<boolean> => {
  if (!formRef.value) return true
  try {
    await formRef.value.validate()
    return true
  } catch {
    return false
  }
}

// 重置表单
const resetForm = () => {
  const currentValues = { ...props.modelValue }
  const resetValues: Record<string, any> = {}
  formItemsWithState.value.forEach(item => {
    if (!item.disabled) {
      let defaultValue: any = ''
      if (item.initialValue !== undefined) {
        defaultValue = item.initialValue
      } else if (item.defaultValue !== undefined) {
        defaultValue = item.defaultValue
      } else {
        switch (item.type) {
          case 'radio':
            defaultValue = item.options?.[0]?.value ?? ''
            break
          case 'select':
            defaultValue = item.multiple ? [] : null
            break
          case 'date':
            defaultValue = null
            break
          case 'textarea':
            defaultValue = ''
            break
          case 'switch':  // 添加 switch 的默认值
            defaultValue = item.inactiveValue !== undefined ? item.inactiveValue : false
            break
          default:
            defaultValue = ''
        }
      }
      resetValues[item.prop] = defaultValue
    } else {
      resetValues[item.prop] = currentValues[item.prop]
    }
  })
  emit('update:modelValue', resetValues)
  setTimeout(() => {
    formRef.value?.clearValidate()
  }, 0)
}

// 处理提交
const handleSubmit = async () => {
  const isValid = await validate()
  if (!isValid) return
  emit('submit', props.modelValue)
}

// 处理取消/重置
const handleCancel = () => {
  resetForm()
  emit('cancel')
}

// ==================== 监听器 ====================
watch(() => props.modelValue, (newVal, oldVal) => {
  if (!newVal) return
  props.formItems.forEach(item => {
    if (item.type === 'select' && item.multiple) {
      const value = newVal[item.prop]
      if (value !== undefined && value !== null && !Array.isArray(value) && value !== oldVal?.[item.prop]) {
        // 将字符串转换为数组
        const arrayValue = typeof value === 'string' ? value.split(',') : [value]
        updateField(item.prop, arrayValue, item)
      }
    }
  })
}, { deep: true, immediate: true })

// ==================== 暴露方法 ====================
defineExpose({
  validate,
  validateField: (prop: string) => formRef.value?.validateField(prop),
  clearValidate: () => formRef.value?.clearValidate(),
  resetForm
})
</script>

<style scoped>
.form-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  background-color: #fefefe;
}

/* ========== 统一表单区域样式 ========== */
.form-container :deep(.el-form) {
  flex: 1;
  width: 100%;
  padding: 16px 5px 16px 5px;
  box-sizing: border-box;
  overflow-y: auto;
  scrollbar-gutter: stable;
  margin: 0;
}

/* 纵向布局 - 单列（使用 gap 统一间距） */
.form-container :deep(.el-form:not(.el-form--inline)) {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-container :deep(.el-form:not(.el-form--inline) .el-form-item) {
  margin: 0;
  padding: 0;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  box-sizing: border-box;
  display: flex;
}

/* ========== 横向/网格布局 - 多列 ========== */
.form-container :deep(.el-form.el-form--inline) {
  display: flex;
  flex-wrap: wrap;
  gap: 18px 20px;
  padding: 16px 20px 16px 20px;
  overflow: visible;
  margin: 0;
}

/* 网格布局 */
.form-container :deep(.el-form.el-form--inline.grid-form) {
  display: grid;
  gap: 18px 20px;
  overflow: visible;
  align-items: start;
  margin: 0;
}

/* 网格列数 */
.form-container :deep(.el-form.el-form--inline.grid-form.cols-2) {
  grid-template-columns: repeat(2, 1fr);
}

.form-container :deep(.el-form.el-form--inline.grid-form.cols-3) {
  grid-template-columns: repeat(3, 1fr);
}

.form-container :deep(.el-form.el-form--inline.grid-form.cols-4) {
  grid-template-columns: repeat(4, 1fr);
}

/* 网格项基础样式 */
.form-container :deep(.el-form.el-form--inline.grid-form .el-form-item.grid-item) {
  margin: 0;
  padding: 0;
  width: 100%;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
}

/* 非网格的横向布局项 */
.form-container :deep(.el-form.el-form--inline:not(.grid-form) .el-form-item) {
  margin: 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
  width: auto;
  flex: 0 1 auto;
  min-height: 36px;
}

/* ========== 统一标签样式 ========== */
.form-container :deep(.el-form-item__label) {
  padding-right: 12px;
  padding-left: 0;
  line-height: 36px;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
  width: auto;
  font-weight: 500;
  color: #606266;
  box-sizing: border-box;
}

/* ========== 统一内容区域样式 ========== */
.form-container :deep(.el-form-item__content) {
  display: flex;
  align-items: center;
  line-height: 36px;
  flex: 1 1 auto;
  min-width: 0;
  margin-left: 0;
}

/* ========== 独占一行的网格项（如文本框） ========== */
.form-container :deep(.el-form.el-form--inline.grid-form .el-form-item.grid-item-full) {
  grid-column: 1 / -1 !important;
  width: 100% !important;
  display: flex !important;
  flex-direction: row !important;
  margin: 0 !important;
  padding: 0 !important;
  align-items: center !important;
}

.form-container :deep(.el-form.el-form--inline.grid-form .el-form-item.grid-item-full .el-form-item__label) {
  flex-shrink: 0;
  text-align: right;
  padding-right: 12px;
  box-sizing: border-box;
  line-height: 36px;
}

.form-container :deep(.el-form.el-form--inline.grid-form .el-form-item.grid-item-full .el-form-item__content) {
  flex: 1 !important;
  margin-left: 0 !important;
  padding-left: 0 !important;
  width: auto !important;
  align-items: center;
}

.form-container :deep(.el-form.el-form--inline.grid-form .el-form-item.grid-item-full .el-textarea) {
  width: 100% !important;
  max-width: 100% !important;
}

/* ========== 控件统一样式 ========== */
.form-container :deep(.el-select),
.form-container :deep(.el-input),
.form-container :deep(.el-date-editor) {
  min-width: 0;
  box-sizing: border-box;
  overflow: visible !important;
  width: 100%;
}

.form-container :deep(.el-select__wrapper),
.form-container :deep(.el-input__wrapper) {
  width: 100% !important;
  padding: 0 12px !important;
  box-sizing: border-box !important;
  border-radius: 4px;
  box-shadow: 0 0 0 1px #dcdfe6 inset;
}

.form-container :deep(.el-select__wrapper:hover),
.form-container :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #c0c4cc inset;
}

.form-container :deep(.el-select__wrapper.is-focused),
.form-container :deep(.el-input__wrapper.is-focused) {
  box-shadow: 0 0 0 1px #409eff inset !important;
}

.form-container :deep(.el-select__wrapper) {
  min-height: 36px !important;
}

.form-container :deep(.el-input__wrapper) {
  min-height: 36px !important;
}

.form-container :deep(.el-input__inner) {
  line-height: 36px !important;
  height: 36px !important;
}

/* 多选select */
.form-container :deep(.el-select__tags) {
  max-width: calc(100% - 20px);
  overflow: hidden;
}

/* 文本域 */
.form-container :deep(.el-textarea__inner) {
  min-height: 60px !important;
  line-height: 1.5;
  resize: vertical;
}

/* 单选框组 */
.form-container :deep(.el-radio-group) {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.form-container :deep(.el-radio) {
  margin-right: 0;
  height: 36px;
  line-height: 36px;
}

.form-container :deep(.el-radio__label) {
  padding-left: 6px;
}

/* 自定义占位符颜色 */
.form-container .custom-placeholder :deep(.el-input__inner::placeholder),
.form-container .custom-placeholder :deep(.el-textarea__inner::placeholder) {
  color: var(--placeholder-color, #c0c4cc) !important;
}

/* 分组下拉样式 */
.group-select-dropdown {
  max-height: 200px;
  overflow-y: auto;
}

.group-header {
  display: flex;
  align-items: center;
  height: 34px;
  padding: 0 32px 0 20px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.group-header:hover {
  background-color: #f5f7fa;
}

.group-header .el-icon {
  transition: transform 0.2s;
  font-size: 14px;
  color: #909399;
  transform: rotate(180deg);
}

.group-header .el-icon.expanded {
  transform: rotate(0deg);
}

.group-title {
  flex: 1;
  color: #606266;
  font-size: 14px;
}

.group-options {
  background-color: #fff;
}

.group-option-item {
  padding-left: 35px !important;
}

:deep(.group-select-popper .el-select-dropdown__item) {
  height: 36px;
  line-height: 36px;
}

:deep(.group-select-popper .el-select-dropdown__item:hover) {
  background-color: #f5f7fa;
}

/* ========== Footer 样式 - 统一样式 ========== */
.form-footer {
  display: flex;
  justify-content: center;
  gap: 24px;
  flex-shrink: 0;
  background-color: #ffffff;
  margin-bottom: 5px;
}

/* 按钮样式 */
.form-footer .el-button {
  min-width: 100px;
  border-radius: 4px;
}

/* 下拉框弹出层样式 */
.form-container :deep(.el-select__popper),
.form-container :deep(.el-date-picker__popper) {
  z-index: 9999 !important;
}

/* 必填星号样式 */
.form-container :deep(.el-form-item.is-required .el-form-item__label::before) {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

/* 动态颜色样式 */
.form-container :deep(.el-input[style*="--control-color"] .el-input__inner) {
  color: var(--control-color) !important;
}

.form-container :deep(.el-textarea[style*="--control-color"] .el-textarea__inner) {
  color: var(--control-color) !important;
}

.form-container :deep(.el-select[style*="--control-color"] .el-select__selected-item) {
  color: var(--control-color) !important;
}

.form-container :deep(.el-input[style*="--control-bg-color"] .el-input__wrapper) {
  background-color: var(--control-bg-color) !important;
}

.form-container :deep(.el-textarea[style*="--control-bg-color"] .el-textarea__inner) {
  background-color: var(--control-bg-color) !important;
}

.form-container :deep(.el-select[style*="--control-bg-color"] .el-select__wrapper) {
  background-color: var(--control-bg-color) !important;
}

.form-container :deep(.el-radio-group[style*="--control-color"] .el-radio__label) {
  color: var(--control-color) !important;
}

.form-container :deep(.el-date-editor[style*="--control-color"] .el-input__inner) {
  color: var(--control-color) !important;
}

.form-container :deep(.el-input.is-disabled[style*="--disabled-color"] .el-input__inner) {
  color: var(--disabled-color) !important;
  -webkit-text-fill-color: var(--disabled-color) !important;
}

/* ========== 手机端响应式设计 ========== */
@media screen and (max-width: 768px) {
  .form-container :deep(.el-form) {
    padding: 12px 16px 12px 0px;
  }
  /* 手机端强制改为单列布局 - 保持原有标签和控件的横排布局 */
  .form-container :deep(.el-form.el-form--inline) {
    display: flex;
    padding: 12px 14px 12px 0px;
    flex-direction: column;
    gap: 10px;
  }
  /* 覆盖所有网格列数为单列 */
  .form-container :deep(.el-form.el-form--inline.grid-form.cols-2),
  .form-container :deep(.el-form.el-form--inline.grid-form.cols-3),
  .form-container :deep(.el-form.el-form--inline.grid-form.cols-4) {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  /* 表单项保持横排布局（标签在左，控件在右） */
  .form-container :deep(.el-form-item) {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    width: 100% !important;
    margin: 0 !important;
  }
  /* 标签样式保持右对齐 */
  .form-container :deep(.el-form-item__label) {
    text-align: right !important;
    padding-right: 5px !important;
    font-size: 13px;
    line-height: 32px;
    flex-shrink: 0;
  }
  /* 内容区域自适应 */
  .form-container :deep(.el-form-item__content) {
    flex: 1 !important;
    margin-left: 0 !important;
  }
  /* 控件宽度自适应 */
  .form-container :deep(.el-select),
  .form-container :deep(.el-input),
  .form-container :deep(.el-date-editor) {
    width: 100% !important;
    flex: 1;
  }
  /* 控件高度缩小 */
  .form-container :deep(.el-select__wrapper),
  .form-container :deep(.el-input__wrapper) {
    min-height: 32px !important;
  }
  .form-container :deep(.el-input__inner) {
    line-height: 32px !important;
    height: 32px !important;
    font-size: 13px;
  }
  /* ========== 调整 placeholder 文字大小 ========== */
  .form-container :deep(.el-input__inner::placeholder) {
    font-size: 12px !important;
  }
  .form-container :deep(.el-textarea__inner::placeholder) {
    font-size: 12px !important;
  }
  .form-container :deep(.el-select__selected-item) {
    font-size: 13px;
    line-height: 32px;
  }
  /* ==========调整 select 下拉选项文字大小 ========== */
  .form-container :deep(.el-select-dropdown__item) {
    font-size: 13px !important;
  }
  /* ========== 调整 radio 标签文字大小 ========== */
  .form-container :deep(.el-radio__label) {
    font-size: 13px !important;
  }
  /* ========== 调整 radio 组整体字体 ========== */
  .form-container :deep(.el-radio-group) {
    font-size: 13px;
  }
  /* 文本域 */
  .form-container :deep(.el-textarea__inner) {
    min-height: 50px !important;
    font-size: 13px;
  }
  /* 单选框组 */
  .form-container :deep(.el-radio-group) {
    flex-wrap: wrap;
    gap: 12px;
  }
  /* 独占一行的项 */
  .form-container :deep(.el-form-item.grid-item-full) {
    width: 100% !important;
  }

  /* 底部按钮 */
  .form-footer {
    padding: 10px 16px;
    gap: 16px;
  }
  .form-footer .el-button {
    min-width: 80px;
    padding: 8px 16px;
    font-size: 13px;
  }
}

/* ========== 小屏手机 ========== */
@media screen and (max-width: 480px) {
  .form-container :deep(.el-form) {
    padding: 10px 12px;
  }
  .form-container :deep(.el-form-item__label) {
    font-size: 12px;
    line-height: 30px;
    padding-right: 8px !important;
  }
  .form-container :deep(.el-select__wrapper),
  .form-container :deep(.el-input__wrapper) {
    min-height: 30px !important;
  }
  .form-container :deep(.el-input__inner) {
    line-height: 30px !important;
    height: 30px !important;
    font-size: 12px;
  }
  /* ========== 小屏手机 placeholder 文字大小 ========== */
  .form-container :deep(.el-input__inner::placeholder) {
    font-size: 11px !important;
  }
  .form-container :deep(.el-textarea__inner::placeholder) {
    font-size: 11px !important;
  }
  /* ========== 小屏手机 select 下拉选项文字大小 ========== */
  .form-container :deep(.el-select-dropdown__item) {
    font-size: 12px !important;
  }
  /* ========== 小屏手机 radio 标签文字大小 ========== */
  .form-container :deep(.el-radio__label) {
    font-size: 12px !important;
  }
  .form-container :deep(.el-select__selected-item) {
    font-size: 12px;
    line-height: 30px;
  }
  .form-container :deep(.el-textarea__inner) {
    min-height: 45px !important;
    font-size: 12px;
  }
  .form-footer {
    padding: 8px 12px;
    gap: 12px;
  }
  .form-footer .el-button {
    min-width: 70px;
    padding: 6px 12px;
    font-size: 12px;
  }
}
</style>
